import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiCreditCard } from 'react-icons/fi';
import { SELF_PACED_PAYWALL_EVENT } from '../services/api';
import { PERSONAL_SEMESTER_PATH } from '../shared/billing/personalLearning';
import './SelfPacedPaywall.css';

// Pages where a learner is already choosing how to pay; the dialog would only
// cover the choice it points to.
const QUIET_PATHS = ['/pricing', '/billing', PERSONAL_SEMESTER_PATH, '/cohorts'];

// Answers the server's "self-paced study needs a plan" refusal from any
// learning screen with the two ways to keep learning. Mobile counterpart:
// mobile/src/components/SelfPacedPaywall.tsx.
function SelfPacedPaywall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // A learner who closes the dialog is not asked again on the same page; the
  // next page that needs a plan asks once more.
  const dismissedPathRef = useRef('');
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const handler = () => {
      const path = pathRef.current;
      if (QUIET_PATHS.some((quiet) => path.startsWith(quiet))) return;
      if (dismissedPathRef.current === path) return;
      setOpen(true);
    };
    window.addEventListener(SELF_PACED_PAYWALL_EVENT, handler);
    return () => window.removeEventListener(SELF_PACED_PAYWALL_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        dismissedPathRef.current = pathRef.current;
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };
  const dismiss = () => {
    dismissedPathRef.current = location.pathname;
    setOpen(false);
  };

  return (
    <div className="self-paced-paywall-overlay" role="dialog" aria-modal="true" aria-labelledby="self-paced-paywall-title">
      <div className="self-paced-paywall">
        <h2 id="self-paced-paywall-title">{t('paywall.title', 'Choose how to keep learning')}</h2>
        <p>{t('paywall.body', 'Subscribe to learn at your own pace, or book a semester that starts on the day you choose.')}</p>
        <div className="self-paced-paywall-actions">
          <button type="button" className="self-paced-paywall-primary" onClick={() => go('/pricing')} autoFocus>
            <FiCreditCard aria-hidden="true" />
            {t('paywall.seePlans', 'See plans')}
          </button>
          <button type="button" className="self-paced-paywall-secondary" onClick={() => go(PERSONAL_SEMESTER_PATH)}>
            <FiCalendar aria-hidden="true" />
            {t('paywall.bookSemester', 'Book a personal semester')}
          </button>
          <button type="button" className="self-paced-paywall-dismiss" onClick={dismiss}>
            {t('paywall.notNow', 'Not now')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SelfPacedPaywall;
